import {ViewController} from "data/types/structure";
import {inject, injectable} from "inversify";
import {makeAutoObservable, observable} from "mobx";
import {ModalType, RequestState} from "data/enums";
import {Bindings} from "data/constants/bindings";
import type {ILocalizationStore} from "data/stores/localization/localization.store";
import type {IUserStore} from "data/stores/user/user.store";
import type {ITeamStore} from "data/stores/team/team.store";
import type {IRound, IRoundsStore} from "data/stores/rounds/rounds.store";
import {findIndex, findLastIndex} from "lodash";
import type {IRankingsStore} from "data/stores/rankings/rankings.store";
import type {IModalsStore} from "data/stores/modals/modals.store";

export interface IRoundNavigatorController extends ViewController {
	get i18n(): ILocalizationStore;
	get gamedays(): IRound[];
	get selectedRoundId(): number;
	get selectedRound(): IRound | undefined;
	get currentRound(): IRound | undefined;
	get hasNextRound(): boolean;
	get hasPrevRound(): boolean;

	onChangeRound: (roundId: number) => void;
	toNextRound: () => void;
	toPrevRound: () => void;
	onCompleteCountdown: () => void;
	onOpenScoreModal: () => void;
}

@injectable()
export class RoundNavigatorController implements IRoundNavigatorController {
	@observable _requestState: RequestState = RequestState.IDLE;

	get i18n() {
		return this._i18nStore;
	}

	get gamedays() {
		return this._roundStore.list;
	}

	get selectedRoundId() {
		return this._roundStore.selectedRoundId;
	}

	get selectedRound() {
		return this._roundStore.selectedRound;
	}

	get currentRound() {
		return this._roundStore.currentRound;
	}

	get hasNextRound() {
		const lastRoundIndex = findLastIndex(this._roundStore.list);

		return this.currentRoundIndex < lastRoundIndex;
	}

	get hasPrevRound() {
		return this.currentRoundIndex > 0;
	}

	private get currentRoundIndex() {
		return findIndex(this._roundStore.list, (el) => el.id === this.selectedRoundId);
	}

	constructor(
		@inject(Bindings.UserStore) private _userStore: IUserStore,
		@inject(Bindings.LocalizationStore) private _i18nStore: ILocalizationStore,
		@inject(Bindings.TeamStore) private _teamStore: ITeamStore,
		@inject(Bindings.RoundsStore) private _roundStore: IRoundsStore,
		@inject(Bindings.RankingsStore) private _rankingStore: IRankingsStore,
		@inject(Bindings.ModalsStore) private _modalStore: IModalsStore
	) {
		makeAutoObservable(this);
	}

	onChangeRound = (roundId: number) => {
		this._roundStore.setSelectedRoundId(roundId);
		this._roundStore.setSelectedRoundSessions();
		this._roundStore.setSelectedSessionId(this._roundStore.selectedRoundSessions[0].id ?? 0);
		void this._rankingStore.fetchGamebar({
			gamedayId: roundId,
		});
	};

	toNextRound = () => {
		const nextRound = this._roundStore.list[this.currentRoundIndex + 1];

		if (this.hasNextRound) {
			this.onChangeRound(nextRound.id ?? 0);
		}
	};

	toPrevRound = () => {
		const prevRound = this._roundStore.list[this.currentRoundIndex - 1];

		if (this.hasPrevRound) {
			this.onChangeRound(prevRound.id ?? 0);
		}
	};

	onCompleteCountdown = () => {
		void this._roundStore.fetchRounds();
	};

	onOpenScoreModal = () => {
		this._modalStore.showModal(ModalType.SCORE_INFO, {
			message: "",
		});
	};

	dispose() {
		return;
	}

	init() {
		return;
	}
}
